import { specValue } from '../cars/carFigures';

/**
 * What each car brings to the line, and what it is carrying (SPEC-008 PR-8.6).
 *
 * The reader is about to watch two dots race and the whole value of it is
 * knowing WHY one of them wins. A briefing before the run is what turns the race
 * from a result into an argument: this one is lighter and puts it down through
 * all four wheels, that one has more power than the road will take.
 *
 * NOTHING HERE IS AUTHORED PER CAR. Each trait is one message key, reused for
 * every pairing and filled with figures the catalogue already carries — the same
 * rule `duelInsight.js` is built on, and for the same reason: a hand-written
 * paragraph per car per opponent is twenty-four times twenty-three pieces of prose
 * nobody has commissioned, and it would go stale the first time a figure was
 * corrected.
 *
 * The traits are RELATIVE and they are CONDITIONAL. "Light" is not a fact about
 * a car, it is a fact about a car next to another one; and all-wheel drive is a
 * footnote in the dry and the whole race on snow. So every trait is asked about
 * a pair and about a road, and the same car briefs differently against a
 * different opponent — which is exactly what a reader comparing two cars wants
 * and is the reason this cannot be a property of a record.
 *
 * Pure: message keys and numbers, never resolved copy.
 */

/** Two of each. A list of five advantages is a list nobody reads to the end. */
const PER_SIDE = 2;

/** Where the engine sits, parsed the way `carDynamics` parses it. */
function enginePosition(car) {
    const stated = car?.specs?.engine?.position;

    return stated || String(car?.specs?.engine?.layout || '').split('-')[0] || 'front';
}

function isTurbocharged(car) {
    return ['turbo', 'twin-turbo'].includes(car?.specs?.engine?.aspiration);
}

/**
 * The traits, in the order they are OFFERED — which is not the order they are
 * shown, because a trait is only offered if it applies.
 *
 * Each answers one question: is this a thing worth telling the reader about
 * THIS car against THIS one on THIS road? A margin is attached to every numeric
 * trait for the reason `duelSpecs` attaches one to every question: two cars four
 * kilograms apart are the same weight, and a briefing that said otherwise would
 * be padding itself out with noise.
 *
 * `weight` measures a `gain` where the others measure a lead, because less is
 * what wins — the direction is the trait's own business and not the caller's.
 */
const TRAITS = [
    {
        key: 'allWheelDrive',
        // The single most decisive fact on this screen, and the one the dry road
        // hides completely. It leads the briefing wherever grip is short.
        applies: (car, rival) => car.specs.drivetrain === 'awd' && rival.specs.drivetrain !== 'awd',
        weight: ({ surface }) => (surface === 'dry' ? 3 : 10),
        values: () => ({}),
    },
    {
        key: 'powerToWeight',
        applies: (car, rival) => lead(car, rival, 'powerToWeight') >= 25,
        weight: () => 6,
        values: (car, rival) => ({
            value: specValue(car, 'powerToWeight'),
            margin: lead(car, rival, 'powerToWeight'),
        }),
    },
    {
        key: 'light',
        applies: (car, rival) => lead(rival, car, 'weight') >= 80,
        // A climb is a fight against the car's own mass, so weight stops being a
        // launch detail and becomes the race.
        weight: ({ gradient }) => (gradient === 'climb' ? 8 : 5),
        values: (car, rival) => ({
            value: specValue(car, 'weight'),
            margin: lead(rival, car, 'weight'),
        }),
    },
    {
        key: 'torque',
        applies: (car, rival) => lead(car, rival, 'torque') >= 60,
        weight: () => 4,
        values: (car, rival) => ({
            value: specValue(car, 'torque'),
            margin: lead(car, rival, 'torque'),
        }),
    },
    {
        key: 'traction',
        // The engine sitting over the driven wheels, against a rival whose is
        // not. It is why a 911 launches like a 911.
        applies: (car, rival) => car.specs.drivetrain === 'rwd'
            && ['mid', 'rear'].includes(enginePosition(car))
            && rival.specs.drivetrain === 'rwd'
            && enginePosition(rival) === 'front',
        weight: () => 3,
        values: () => ({}),
    },
    {
        key: 'topSpeed',
        // Only ever worth mentioning where the strip is long enough to reach for
        // it, which on a low-grip road it never is.
        applies: (car, rival) => lead(car, rival, 'topSpeed') >= 15,
        weight: ({ surface }) => (surface === 'dry' ? 3 : 1),
        values: (car, rival) => ({
            value: specValue(car, 'topSpeed'),
            margin: lead(car, rival, 'topSpeed'),
        }),
    },
];

/**
 * The weaknesses, which are NOT simply the rival's strengths inverted.
 *
 * One of them is a fact about the car and the ROAD rather than about the pairing
 * at all: power the surface will not take is wasted whoever is in the other
 * lane, and it is invisible in any table of who leads what. The rest are
 * comparative like the strengths above, including the turbo's — lag is only a
 * disadvantage against something that does not have it, and two turbos waiting
 * for boost together are not losing anything to each other.
 */
const FLAWS = [
    {
        key: 'wheelspin',
        // Rear drive, a lot of power for the weight, and a road that cannot take
        // it. This is the one the reader is about to watch happen.
        applies: (car, rival, { surface, tractionControl }) => car.specs.drivetrain !== 'awd'
            && specValue(car, 'powerToWeight') >= 250
            && (surface !== 'dry' || tractionControl === 'off'),
        weight: ({ surface }) => (surface === 'dry' ? 6 : 9),
        values: (car) => ({ value: specValue(car, 'powerToWeight') }),
    },
    {
        key: 'lag',
        applies: (car, rival) => isTurbocharged(car) && !isTurbocharged(rival),
        weight: () => 4,
        values: () => ({}),
    },
    {
        key: 'heavy',
        applies: (car, rival) => lead(car, rival, 'weight') >= 80,
        weight: ({ gradient }) => (gradient === 'climb' ? 8 : 4),
        values: (car, rival) => ({
            value: specValue(car, 'weight'),
            margin: lead(car, rival, 'weight'),
        }),
    },
    {
        key: 'frontEngine',
        applies: (car, rival) => car.specs.drivetrain === 'rwd'
            && enginePosition(car) === 'front'
            && ['mid', 'rear'].includes(enginePosition(rival)),
        weight: () => 3,
        values: () => ({}),
    },
];

/** How far `car` is ahead of `rival` on a figure, or 0 where it is not. */
function lead(car, rival, spec) {
    const mine = specValue(car, spec);
    const theirs = specValue(rival, spec);

    return mine === null || theirs === null ? 0 : Math.max(0, mine - theirs);
}

function pick(table, car, rival, conditions) {
    return table
        .filter((trait) => trait.applies(car, rival, conditions))
        .map((trait) => ({
            key: trait.key,
            rank: trait.weight(conditions),
            values: trait.values(car, rival),
        }))
        .sort((one, other) => other.rank - one.rank)
        .slice(0, PER_SIDE);
}

/**
 * Both cars, briefed against each other on this road.
 *
 * Returns per side a list of `strengths` and a list of `flaws`, each entry a
 * message key and its substitutions. Either list can be EMPTY and that is a
 * real answer rather than a gap to fill: two cars that are the same weight, the
 * same layout and within twenty horsepower per tonne of each other have nothing
 * to say to a reader before the lights, and inventing a sentence for them would
 * be the panel padding itself.
 */
export function briefCars(cars, conditions) {
    if (!Array.isArray(cars) || cars.length !== 2 || cars.some((car) => !car)) {
        return [];
    }

    return cars.map((car, index) => {
        const rival = cars[1 - index];

        return {
            carId: car.id,
            strengths: pick(TRAITS, car, rival, conditions),
            flaws: pick(FLAWS, car, rival, conditions),
        };
    });
}
